/**
 * "Đối chiếu lại với MISA" cho một đợt nhập hoá đơn bằng AI.
 *
 * Backend chạy ở worker nền (kéo khoảng ngày của đợt từ MISA về, rồi khớp lại
 * từng dòng) và ghi tiến độ lên `batch.doiChieu`; trang cha poll batch và đưa
 * `doiChieu` xuống đây. Modal chỉ hiển thị — không tự gọi API.
 */
import React, { useMemo } from 'react';
import {
  Alert, Badge, Button, Modal, ProgressBar, Spinner, Table,
} from 'react-bootstrap';
import { TRANG_THAI_MISA } from '../utils/trangThaiMisa';

const HuyHieu = ({ k }) => {
  const t = TRANG_THAI_MISA[k] || TRANG_THAI_MISA['chua-kiem'];
  return <Badge bg={t.bg} text={t.bg === 'warning' ? 'dark' : undefined} title={t.hint}>{t.nhan}</Badge>;
};

const kyHieuSo = (it, soDuPhong) => {
  const g = (f) => (it?.corrections && Object.prototype.hasOwnProperty.call(it.corrections, f)
    ? it.corrections[f] : it?.extracted?.[f]);
  return `${g('Series') || '—'} / ${g('InvoiceNo') || soDuPhong || '—'}`;
};

const gio = (v) => (v ? new Date(v).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '');
const ngay = (v) => (v ? new Date(v).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }) : '');

export default function DoiChieuMisaModal({
  show, doiChieu, items, dangGui = false, onBatDau, onDong,
}) {
  const dangChay = doiChieu?.trangThai === 'dang-chay';
  const xong = doiChieu?.trangThai === 'xong';
  const theoId = useMemo(() => new Map((items || []).map((it) => [String(it._id), it])), [items]);
  const pct = dangChay && doiChieu?.tong ? Math.round(((doiChieu.xong || 0) * 100) / doiChieu.tong) : 0;

  let nhanNut = 'Bắt đầu đối chiếu';
  if (dangChay || dangGui) nhanNut = 'Đang đối chiếu…';
  else if (doiChieu) nhanNut = 'Đối chiếu lại';

  return (
    <Modal show={show} onHide={onDong} size='lg' scrollable>
      <Modal.Header closeButton>
        <Modal.Title as='h6'>Đối chiếu lại với MISA</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='small text-muted'>
          Kéo hoá đơn trong khoảng ngày của đợt này từ MISA về, rồi khớp lại từng dòng chưa ghi.
          Dòng chưa có trong dữ liệu đồng bộ thì tra MISA theo số hoá đơn. Dòng đã ghi vào hệ thống
          không bị đụng; dòng đã duyệt chỉ cập nhật trạng thái, không bị đổi số liệu.
        </p>

        {doiChieu?.trangThai === 'loi' && <Alert variant='danger' className='py-2'>{doiChieu.loiMoTa || 'Đối chiếu lỗi'}</Alert>}

        {dangChay && (
          <div className='mb-3'>
            <div className='small mb-1'>
              <Spinner size='sm' animation='border' className='me-2' />
              {doiChieu.buoc === 'dong-bo'
                ? 'Đang kéo hoá đơn từ MISA về (vài phút cho khoảng vài tháng)…'
                : `Đang đối chiếu ${doiChieu.tong == null ? '' : `${doiChieu.xong || 0} / ${doiChieu.tong} dòng`}`}
            </div>
            {doiChieu.tong && doiChieu.buoc !== 'dong-bo' ? <ProgressBar now={pct} /> : null}
          </div>
        )}

        {xong && (
          <>
            <div className='mb-2'>
              <span className='me-3'>{doiChieu.khop || 0} khớp</span>
              <span className='me-3'>{doiChieu.khongKhop || 0} không khớp</span>
              <span className='me-3'>{doiChieu.loi || 0} lỗi tra</span>
              <span className='text-muted small'>/ {doiChieu.tong || 0} dòng · xong lúc {gio(doiChieu.ketThuc)}</span>
            </div>
            {doiChieu.dongBo && (
              <div className='small text-muted mb-2'>
                Đã đồng bộ MISA từ {ngay(doiChieu.dongBo.tu)} đến {ngay(doiChieu.dongBo.den)}
                {doiChieu.dongBo.soHoaDon != null && ` (${doiChieu.dongBo.soHoaDon} hoá đơn)`}.
              </div>
            )}
            {doiChieu.canhBao && <Alert variant='warning' className='py-2 small'>{doiChieu.canhBao}</Alert>}
            {(doiChieu.ketQua || []).length > 0 && (
              <Table size='sm' className='align-middle'>
                <thead className='table-light'>
                  <tr><th>Ký hiệu / Số</th><th>Trước</th><th>Sau</th></tr>
                </thead>
                <tbody>
                  {doiChieu.ketQua.map((r) => (
                    <tr key={String(r.item)} className={r.truoc !== r.sau ? 'table-info' : undefined}>
                      <td>{kyHieuSo(theoId.get(String(r.item)), r.soHoaDon)}</td>
                      <td><HuyHieu k={r.truoc} /></td>
                      <td><HuyHieu k={r.sau} /></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' onClick={onDong}>Đóng</Button>
        <Button onClick={onBatDau} disabled={dangChay || dangGui}>{nhanNut}</Button>
      </Modal.Footer>
    </Modal>
  );
}
